import { getListingMember } from 'app/lib/guardian';
import { TrustCircleListingBanner } from './trust-circle-listing-banner';
import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { GeoPin } from 'app/design/icons/geo-pin';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useListingCard } from 'app/hooks/use-listing-card';
import { getListingCatalogLabels } from 'app/lib/listing-catalog-labels';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { BonificationBadge } from 'app/screens/billboard/all-listings/components/bonification-banner';
import type { ListingOfferCardProps } from 'app/types/listing-offer-card-props';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { CorpoImage } from './corp-image';
import { FavouriteButton } from './favourite-button';
import { ListingCardAuthorStats } from './listing-card-author-stats';
import { LoanFeeTag } from './listing-card-loan-fee-tag';
import { ListingPostedByMembers } from './listing-card-posted-by-members';
import { ListingThumbnail } from './listing-thumbnail';
import { Checkbox, createTheme as createDSTheme } from 'app/shopify';
import { IconEyeOff } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { ListingCardBadges } from './listing-card-metadata';
import { Listing, ListingSubtype, ListingType } from 'app/types/listing';
import { OFFER_CARD_HEIGHT } from './listing-offer-card-old.small.constants';
const theme = createDSTheme();

function ListingOfferCardSmallOld(props: ListingOfferCardProps) {
  const {
    continueTutorial,
    hideCorpoBadge = false,
    isFirst,
    isNew,
    isSelecting,
    listing,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
  } = props;
  const { t } = useTranslation('all-listings');
  const { categoryPrimary, item, type, subtype, before, groups, distance } = listing;

  const {
    isAuthor,
    isInvisible,
    tutorialViewRef,
    isSelected,
    displayNumViews,
    displayNumFavourites,
    thumbPicture,
    onPress,
    loanFeeAvailable,
  } = useListingCard({
    continueTutorial,
    isFirst,
    isSelecting,
    listing,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
  });

  const { formatDistanceWithoutText } = useDistanceUnit();
  const titleSizeBeforeOverflow = 40;
  const listingCardTestID = `listing-offer-card-${item.name}`;
  const displayedIsNew = isAuthor ? false : isNew;

  return (
    <Pressable
      accessibilityRole={isSelecting ? 'checkbox' : 'button'}
      accessibilityState={isSelecting ? { checked: Boolean(isSelected) } : undefined}
      onPress={onPress}
      ref={tutorialViewRef}
      testID={listingCardTestID}
    >
      <Row alignItems="center" gap="spacing.general.x4">
        {isSelecting ? (
          <View
            pointerEvents="none"
            style={{ height: OFFER_CARD_HEIGHT, width: 24, flexDirection: 'row', alignItems: 'center' }}
          >
            <Checkbox selected={Boolean(isSelected)} showText={false} />
          </View>
        ) : null}
        <SmallOfferThumbnail
          isInvisible={isInvisible}
          before={before}
          listing={listing}
          thumbPicture={thumbPicture}
          subtype={subtype}
          type={type}
          displayedIsNew={displayedIsNew}
          groups={groups}
          hideCorpoBadge={hideCorpoBadge}
          isAuthor={isAuthor}
        />

        <SmallOfferDetails
          listing={listing}
          isAuthor={isAuthor}
          item={item}
          titleSizeBeforeOverflow={titleSizeBeforeOverflow}

          distance={distance}
          formatDistanceWithoutText={formatDistanceWithoutText}
          displayNumViews={displayNumViews}
          displayNumFavourites={displayNumFavourites}
          categoryPrimary={categoryPrimary}
          t={t}
          loanFeeAvailable={loanFeeAvailable}
        />
      </Row>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  hiddenListingOverlay: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export { ListingOfferCardSmallOld };

function SmallOfferDetails({
  listing,
  isAuthor,
  item,
  titleSizeBeforeOverflow,
  distance,
  formatDistanceWithoutText,
  displayNumViews,
  displayNumFavourites,
  categoryPrimary,
  t,
  loanFeeAvailable,
}: {
  listing: Listing;
  isAuthor: boolean;
  item: { id: string; name: string };
  titleSizeBeforeOverflow: number;
  distance: number | undefined;
  formatDistanceWithoutText: (distanceInMeters?: number) => string;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  categoryPrimary: Listing['categoryPrimary'];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
  loanFeeAvailable: boolean;
}) {
  return (
    <Column flex={1} gap="spacing.general.x2">
      {listing.canBonify && isAuthor ? (
        <Row>
          <BonificationBadge />
          <View style={{ flex: 1 }} />
        </Row>
      ) : null}
      <Row alignItems="center" gap="spacing.general.x2">
        <Text fontWeight="700" numberOfLines={2} variant="bodyLarge" style={{ flexShrink: 1 }}>
          {item.name.length <= titleSizeBeforeOverflow
            ? item.name
            : `${item.name.slice(0, titleSizeBeforeOverflow)}...`}
        </Text>
        <View style={{ flex: 1 }} />
        {!isAuthor ? <FavouriteButton listing={listing} /> : null}
      </Row>
      <SmallOfferTags
        isAuthor={isAuthor}

        listing={listing}
        distance={distance}
        formatDistanceWithoutText={formatDistanceWithoutText}
      />

      <SmallOfferOwnerStats
        isAuthor={isAuthor}
        displayNumViews={displayNumViews}
        displayNumFavourites={displayNumFavourites}
        categoryPrimary={categoryPrimary}
        t={t}
      />
      {isAuthor && loanFeeAvailable && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}

      {!isAuthor ? (
        <Row alignItems="center" gap="spacing.general.x2">
          <ListingPostedByMembers
            avatarSize={loanFeeAvailable ? 24 : 30}
            grouping={listing.grouping}
            isAuthor={false}
            loanFee={listing.loanFee}
            user={getListingMember(listing)}
          />
        </Row>
      ) : null}

      {loanFeeAvailable && !isAuthor && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}
    </Column>
  );
}

function SmallOfferOwnerStats({
  isAuthor,
  displayNumViews,
  displayNumFavourites,
  categoryPrimary,
  t,
}: {
  isAuthor: boolean;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  categoryPrimary: Listing['categoryPrimary'];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}): React.ReactNode {
  return isAuthor ? (
    Number.isInteger(displayNumViews) && Number.isInteger(displayNumFavourites) ? (
      <ListingCardAuthorStats numViews={displayNumViews} numFavourites={displayNumFavourites} />
    ) : (
      <Row alignItems="center" paddingVertical="spacing.general.x0,5">
        <Row
          alignItems="center"
          borderRadius="radius.xl"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
          style={{ backgroundColor: categoryPrimary.colorLight }}
        >
          <Text
            fontSize={12}
            fontWeight="600"
            paddingHorizontal="spacing.general.x1"
            style={{ color: categoryPrimary.colorDark }}
          >
            {t('manageOffer', { ns: 'common' })}
          </Text>
        </Row>
      </Row>
    )
  ) : null;
}

function SmallOfferTags({
  isAuthor,
  listing,
  distance,
  formatDistanceWithoutText,
}: {
  isAuthor: boolean;
  listing: Listing;
  distance: number | undefined;
  formatDistanceWithoutText: (distanceInMeters?: number) => string;
}): React.ReactNode {
  const { featureFlags } = useListingCatalog();
  const labels = getListingCatalogLabels(listing, featureFlags);
  const hasTags = labels.length > 0;
  return !isAuthor ? (
    <Row
      alignItems="center"
      flexWrap="wrap"
      columnGap="spacing.general.x1"
      style={{ rowGap: theme.spacing['spacing.general.x2'] }}
    >
      {hasTags ? (
        <Row
          alignItems="center"
          backgroundColor="surface.tertiary-neutral"
          borderRadius="radius.xl"
          maxWidth="100%"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
        >
          <Text color="text.secondary" fontSize={12} fontWeight="500" numberOfLines={1}>
            {labels[0]?.label}
          </Text>
        </Row>
      ) : null}
      {hasTags && labels.length > 1 ? (
        <Row
          alignItems="center"
          backgroundColor="surface.tertiary-neutral"
          borderRadius="radius.xl"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
        >
          <Text color="text.secondary" fontSize={12}>
            +{labels.length - 1}
          </Text>
        </Row>
      ) : null}
      {distance !== undefined ? (
        <Row alignItems="center" style={{ gap: 1 }}>
          <GeoPin color={theme.colors['icon.secondary']} height={12} width={12} />
          <Text fontSize={12} style={{ color: theme.colors['icon.secondary'] }}>
            {formatDistanceWithoutText(distance)}
          </Text>
        </Row>
      ) : null}
    </Row>
  ) : null;
}

function SmallOfferThumbnail({
  isInvisible,
  before,
  listing,
  thumbPicture,
  subtype,
  type,
  displayedIsNew,
  groups,
  hideCorpoBadge,
  isAuthor,
}: {
  isInvisible: boolean;
  before: string | null | undefined;
  listing: Listing;
  thumbPicture: string | undefined;
  subtype: ListingSubtype;
  type: ListingType;
  displayedIsNew: boolean | undefined;
  groups: Listing['groups'];
  hideCorpoBadge: boolean;
  isAuthor: boolean;
}) {
  return (
    <Column width={OFFER_CARD_HEIGHT} borderRadius="radius.md" overflow="hidden">
      <View style={{ position: 'relative' }}>
        <View style={{ opacity: isInvisible ? 0.3 : 1 }}>
          <ListingThumbnail
            style={listing.isTrustCircleOnly ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}
            before={before}
            isFound={!!listing.lender}
            picture={thumbPicture}
            subtype={subtype}
            type={type}
            size={OFFER_CARD_HEIGHT}
          />
        </View>
        {isInvisible ? (
          <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.hiddenListingOverlay]}>
            <IconEyeOff strokeWidth={ICON_STROKE_WIDTH} size={44} />
          </View>
        ) : null}
        {!isInvisible ? (
          <ListingCardBadges
            isNew={displayedIsNew}
            listing={listing}
            showNearby={!isAuthor}
            topRightBadge={
              groups?.length && !hideCorpoBadge && !listing.isTrustCircleOnly ? (
                <CorpoImage picture={groups[0]?.picture} />
              ) : null
            }
          />
        ) : null}
      </View>
      <TrustCircleListingBanner listing={listing} />
    </Column>
  );
}
